import { cx } from "@emotion/css";
import InputAdornment from "@mui/material/InputAdornment";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextBlackButton } from "src/components/Default/LekUI/Button";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import Box from "src/components/Default/Main/Box";
import { EmptyTableBox } from "src/components/Main/EmptyBox";
import { Header } from "src/components/Main/ExamsBox/ExamsBox";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import CourseActions from "src/components/NewComponents/menuActions/CourseActions";
import ExamListSkeleton from "src/components/NewComponents/Skeletons/ExamsList";
import CourseView from "src/components/NewComponents/Views/ScoreViews/CourseView";
import { StyledTableCell } from "src/components/Table/DefaultTable";
import Table from "src/components/Table/Table";
import { calculateCourseScore } from "src/helpers/calculateExamScore";
import { useGetCourses, useRemoveCourses } from "src/hooks/useCourses";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";
import CourseIcon from "src/images/Icons/newIcons/navigation/courses.svg";
import SearchIcon from "src/images/Icons/newIcons/search.svg";

interface CourseRow {
	id: string;
	name: string;
	updatedAt: string;
	lessons: { status: string }[];
}

export function CoursesList() {
	const { t } = useTranslation();

	const headers = [
		{},
		{ key: t("table.courses.headers.name") },
		{ key: t("table.courses.headers.progress") },
		{
			key: t("table.courses.headers.date"),
			value: "updatedAt",
			getSortKey: (row: Record<string, unknown>) =>
				new Date(row.updatedAt as string).getTime(),
		},
		{ key: t("table.courses.headers.countOfLessons") },
		{ key: t("table.courses.headers.actions") },
	];
	const { openModal } = useModals();
	const [searchValue, setSearchValue] = useState("");
	const { isLoading, data: rows } = useGetCourses() as {
		isLoading: boolean;
		data?: CourseRow[];
	};
	const { mutateAsync: removeCoursesMutation } =
		useRemoveCourses() as unknown as {
			mutateAsync: (ids: string[]) => Promise<void>;
		};
	const [selectedElements, setSelectedElements] = useState<CourseRow[]>([]);

	const getCountOfAllItems = (row: CourseRow) => {
		return row.lessons.length;
	};

	const getCountOfResolvedItems = (row: CourseRow) => {
		return row.lessons.filter((lesson) => lesson.status === "complete").length;
	};

	const removeCourses = () => {
		openModal(ModalsNames.DeleteItemModal, {
			title: t("modals.deleteItem.courses.title", "Usunąć kursy?"),
			description: t(
				"modals.deleteItem.courses.description",
				"Wybrane kursy zostaną trwale usunięte razem z postępem nauki. Tej operacji nie można cofnąć.",
			),
			confirmText: t("modals.deleteItem.courses.confirm", "Usuń kursy"),
			itemLabel: t("modals.deleteItem.courses.count", {
				count: selectedElements.length,
			}),
			onDelete: () =>
				removeCoursesMutation(selectedElements.map((el) => el.id)),
		});
	};

	const renderRow = (row: CourseRow) => {
		const updated = dayjs(new Date(row.updatedAt)).format(
			"DD-MM-YYYY HH:mm:ss",
		);
		const progress = getCountOfResolvedItems(row);
		const allItems = getCountOfAllItems(row);
		const resOfItems = allItems - progress;
		const score = calculateCourseScore(row) || 0;

		return (
			<>
				<StyledTableCell title={row.name} align="center">
					{row.name}
				</StyledTableCell>
				<StyledTableCell align="center">{score}%</StyledTableCell>
				<StyledTableCell title={"Date"} align="center">
					{updated}
				</StyledTableCell>
				<StyledTableCell
					title={t("table.courses.headers.countOfLessons")}
					align="center"
				>
					{resOfItems}
				</StyledTableCell>
			</>
		);
	};

	const onSelectActions = () => {
		return [
			{
				title: t("table.courses.actions.removeCourses"),
				onClick: removeCourses,
				Icon: <DeleteIcon />,
			},
		];
	};

	const onRowSelected = (row: CourseRow) => {
		if (selectedElements.some((element) => element.id === row.id)) {
			setSelectedElements(
				selectedElements.filter((element) => element.id !== row.id),
			);
		} else {
			setSelectedElements([...selectedElements, row]);
		}
	};

	const renderAdditionalActions = () => {
		const disabled = !selectedElements.length;

		return (
			<div className="table-additional-actions">
				<span>
					{t("table.courses.actions.title")} (
					<label>{selectedElements.length}</label>):{" "}
				</span>
				<TextBlackButton
					startIcon={<DeleteIcon className="icon-small" />}
					disabled={disabled}
					onClick={removeCourses}
					className={cx({
						"icon-error": !disabled,
					})}
				>
					{t("table.courses.actions.removeCourses")}
				</TextBlackButton>
			</div>
		);
	};

	const renderSearch = () => {
		return (
			<TextFieldGrey
				variant="filled"
				placeholder={t("commands.search")}
				value={searchValue}
				onChange={(e) => setSearchValue(e.target.value)}
				className="w-100 search--text-field"
				slotProps={{
					htmlInput: { "data-test-id": "courses-list-search-input" },
					input: {
						disableUnderline: true,
						startAdornment: (
							<InputAdornment position="start" className="m-0">
								<SearchIcon className="icon-normal icon-black-80" />
							</InputAdornment>
						),
					},
				}}
			/>
		);
	};

	const renderContent = () => {
		if (isLoading) {
			return <ExamListSkeleton columns={headers.length - 1} rows={10} />;
		}

		return (
			<Table
				rows={rows}
				headers={headers}
				selectedElements={selectedElements}
				renderRow={renderRow}
				searchValue={searchValue}
				sortKey="name"
				defaultOrderBy="createdAt"
				getOnSelectActions={onSelectActions}
				getCountOfAllElements={getCountOfAllItems}
				getCountOfResolvedItems={getCountOfResolvedItems}
				getRowName={(row: CourseRow) => row.name}
				getRowProps={(row: CourseRow) => ({
					"data-test-id": "course-list-item",
					"data-course-id": row.id,
				})}
				progressText={t("units.lessons")}
				onRowSelected={onRowSelected}
				clearSelectedRows={() => setSelectedElements([])}
				renderMobileRow={(row: CourseRow) => (
					<CourseView row={row} className="" />
				)}
				RowActions={CourseActions}
				EmptyBox={<EmptyTableBox title={t("table.courses.empty.title")} />}
			/>
		);
	};

	return (
		<div className="creator-container">
			<Box
				tabsClassName="basic-box--table-tabs"
				headerClassName="mb-0"
				Header={
					<div>
						<Header
							title={t("table.courses.title")}
							icon={<CourseIcon className={cx("icon-super-big icon-logo")} />}
							withBackButton
						/>
						{renderAdditionalActions()}
					</div>
				}
				additionalTabsActions={renderSearch}
				tabs={[t("table.courses.tabs.courses")]}
				selectedTab={0}
			>
				{renderContent()}
			</Box>
		</div>
	);
}
